// Speakers section for the event detail page.
// Owns its own fetch + inline create/edit/delete state. Falls back to the
// initial list provided by the parent (used when the worker is unreachable
// in dev so the screen still renders mock data).

import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api, ApiError } from '../../lib/api';

const EMPTY_FORM = { name: '', role: '', organisation: '', isTeam: false };

function toForm(speaker) {
  return {
    name: speaker.name ?? '',
    role: speaker.role ?? '',
    organisation: speaker.organisation ?? '',
    isTeam: !!speaker.isTeam,
  };
}

function toRequestBody(form) {
  return {
    name: form.name.trim(),
    role: form.role.trim() || null,
    organisation: form.organisation.trim() || null,
    isTeam: !!form.isTeam,
  };
}

export function SpeakerList({ eventId, initial, readOnly }) {
  const { t } = useTranslation();
  const [speakers, setSpeakers] = useState(initial ?? []);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch fresh from the API on mount. If the worker is unreachable, fall back
  // to whatever the parent passed in (mock data in dev).
  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    api.speakers
      .list(eventId)
      .then((data) => {
        if (cancelled) return;
        setSpeakers(Array.isArray(data?.speakers) ? data.speakers : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[speakers] list failed, keeping initial:', err.message);
      });
    return () => { cancelled = true; };
  }, [eventId, readOnly]);

  function startAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setAdding(true);
  }

  function startEdit(speaker) {
    setAdding(false);
    setEditingId(speaker.id);
    setForm(toForm(speaker));
    setError(null);
  }

  function cancel() {
    setAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (submitting) return;
    if (!form.name.trim()) {
      setError(t('events.detail.speakerForm.nameRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (editingId) {
        const updated = await api.speakers.update(eventId, editingId, toRequestBody(form));
        setSpeakers((prev) => prev.map((s) => (s.id === editingId ? updated : s)));
      } else {
        const created = await api.speakers.create(eventId, toRequestBody(form));
        setSpeakers((prev) => [...prev, created]);
      }
      cancel();
    } catch (err) {
      console.error('[speakers] save failed:', err);
      setError(err instanceof ApiError ? err.message : t('events.detail.speakerForm.saveError'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(speaker) {
    if (!window.confirm(t('events.detail.speakerForm.confirmDelete', { name: speaker.name }))) return;
    try {
      await api.speakers.delete(eventId, speaker.id);
      setSpeakers((prev) => prev.filter((s) => s.id !== speaker.id));
      if (editingId === speaker.id) cancel();
    } catch (err) {
      console.error('[speakers] delete failed:', err);
      window.alert(err instanceof ApiError ? err.message : t('events.detail.speakerForm.deleteError'));
    }
  }

  return (
    <section className="list-section">
      <header className="list-section__header">
        <h3 className="list-section__title">{t('events.detail.sections.speakers')}</h3>
        {!adding && !editingId && !readOnly && (
          <button type="button" className="list-section__add" onClick={startAdd}>
            {t('events.detail.add.speaker')}
          </button>
        )}
      </header>

      {adding && !editingId && (
        <SpeakerForm
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onCancel={cancel}
          submitting={submitting}
          error={error}
          mode="create"
        />
      )}

      <ul>
        {speakers.map((speaker) =>
          editingId === speaker.id ? (
            <li key={speaker.id}>
              <SpeakerForm
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                onCancel={cancel}
                submitting={submitting}
                error={error}
                mode="edit"
                onDelete={() => handleDelete(speaker)}
              />
            </li>
          ) : (
            <li key={speaker.id} className="list-row">
              <button
                type="button"
                className="list-row__body list-row__body--button"
                onClick={() => !readOnly && startEdit(speaker)}
                disabled={readOnly}
              >
                <div className="list-row__primary">
                  {speaker.name}
                  {speaker.isTeam && (
                    <span className="type-badge type-badge--team">
                      {t('events.detail.speakerForm.team')}
                    </span>
                  )}
                </div>
                {(speaker.role || speaker.organisation) && (
                  <div className="list-row__secondary">
                    {speaker.role}
                    {speaker.role && speaker.organisation ? ' · ' : ''}
                    {speaker.organisation}
                  </div>
                )}
              </button>
              {!readOnly && (
                <div className="list-row__actions">
                  <button
                    type="button"
                    className="list-row__action list-row__action--edit"
                    onClick={() => startEdit(speaker)}
                    aria-label={`${t('common.edit')} ${speaker.name}`}
                    title={t('common.edit')}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="list-row__action"
                    onClick={() => handleDelete(speaker)}
                    aria-label={`${t('common.remove')} ${speaker.name}`}
                    title={t('common.remove')}
                  >
                    ✕
                  </button>
                </div>
              )}
            </li>
          ),
        )}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline create/edit form
// ─────────────────────────────────────────────────────────────────────────────

function SpeakerForm({ form, setForm, onSubmit, onCancel, submitting, error, mode, onDelete }) {
  const { t } = useTranslation();

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <form className="inline-form" onSubmit={onSubmit}>
      <div className="inline-form__field">
        <label className="inline-form__label">{t('events.detail.speakerForm.name')}</label>
        <input
          type="text"
          className="inline-form__control"
          value={form.name}
          autoFocus
          onChange={(e) => update('name', e.target.value)}
        />
      </div>

      <div className="inline-form__row">
        <div className="inline-form__field">
          <label className="inline-form__label">{t('events.detail.speakerForm.role')}</label>
          <input
            type="text"
            className="inline-form__control"
            value={form.role}
            onChange={(e) => update('role', e.target.value)}
          />
        </div>
        <div className="inline-form__field">
          <label className="inline-form__label">{t('events.detail.speakerForm.organisation')}</label>
          <input
            type="text"
            className="inline-form__control"
            value={form.organisation}
            onChange={(e) => update('organisation', e.target.value)}
          />
        </div>
      </div>

      <label className="inline-form__checkbox">
        <input
          type="checkbox"
          checked={form.isTeam}
          onChange={(e) => update('isTeam', e.target.checked)}
        />
        {t('events.detail.speakerForm.isTeam')}
      </label>

      {error && <div className="inline-form__error">{error}</div>}

      <div className="inline-form__actions">
        {mode === 'edit' && onDelete && (
          <button
            type="button"
            className="button button--ghost inline-form__delete"
            onClick={onDelete}
            disabled={submitting}
          >
            {t('common.delete')}
          </button>
        )}
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting
            ? t('common.saveSaving')
            : mode === 'edit'
              ? t('common.save')
              : t('events.detail.speakerForm.add')}
        </button>
      </div>
    </form>
  );
}
